/**
 * 계정 얼굴 **두 원장 겹치기**의 순수 단계 — 발굴 색인(공짜) 위에 실시간 조회 창구(유료)의 답을 얹는다.
 *
 * 🔑 브라우저 훅(`useSearchAllAccountProfiles` · `useAccountProfileLookup`)과 공유 페이지(서버)가 같은 단계를 밟는다.
 * ⚠️ 창구는 **색인에 얼굴이 없는 계정만** 묻는다 · 한 번에 `PROFILE_LOOKUP_BATCH_MAX` 건.
 */

import type { ProfileLookupResult, ProfileLookupChannel } from 'src/types/influencer/profile-lookup';

import { PROFILE_LOOKUP_CHANNELS, PROFILE_LOOKUP_BATCH_MAX } from 'src/types/influencer/profile-lookup';

import { profileKey, type AccountProfile } from './search-all-media-cards';

/**
 * 창구에 물을 계정 — 아는 채널 · 색인에 얼굴이 없음 · `skip` 에 없음.
 * @param skip 이미 채웠거나 그만 묻기로 한 열쇠
 */
export function toLookupMissing(
  accounts: { channel: string; account: string }[],
  ledger: Map<string, AccountProfile>,
  skip: (key: string) => boolean = () => false,
): { channel: ProfileLookupChannel; account: string }[] {
  return accounts
    .filter((row) => PROFILE_LOOKUP_CHANNELS.includes(row.channel as ProfileLookupChannel))
    .filter((row) => {
      const key = profileKey(row.channel, row.account);
      return !ledger.get(key)?.avatar && !skip(key);
    })
    .slice(0, PROFILE_LOOKUP_BATCH_MAX)
    .map((row) => ({ channel: row.channel as ProfileLookupChannel, account: row.account }));
}

/**
 * 창구의 답 한 줄 → 얼굴. 창구가 주는 얼굴은 늘 자사 미러다(`profilePicS3Url`).
 * ⚠️ pending·unavailable 에도 값이 실려 올 수 있다(색인에 팔로워만 있는 경우) — 둘 다 없을 때만 `null`.
 */
export function lookupResultToProfile(row: ProfileLookupResult): AccountProfile | null {
  if (!row.profilePicS3Url && row.followerCount == null) return null;
  return { avatar: row.profilePicS3Url, followers: row.followerCount, mirrored: Boolean(row.profilePicS3Url) };
}

/** 색인 위에 창구의 답을 얹는다 — 색인 값을 덮지 않고 **빈자리만** 메운다 */
export function overlayProfiles(ledger: Map<string, AccountProfile>, filled: Map<string, AccountProfile>): Map<string, AccountProfile> {
  if (filled.size === 0) return ledger;
  const out = new Map(ledger);
  filled.forEach((profile, key) => {
    const was = out.get(key);
    const avatar = was?.avatar || profile.avatar;
    out.set(key, {
      avatar,
      followers: was?.followers ?? profile.followers,
      mirrored: avatar === was?.avatar ? Boolean(was?.mirrored) : profile.mirrored,
    });
  });
  return out;
}
